<div class="patient-details-page">
  <!-- Error State -->
  <div *ngIf="errorMessage" class="error-state">
    <mat-icon>error_outline</mat-icon>
    <h3>Error Loading Patient Details</h3>
    <p>{{ errorMessage }}</p>
    <div class="error-actions">
      <button class="back-btn" (click)="goBack()">Back to Search Results</button>
      <button class="new-search-btn" (click)="startNewSearch()">New Search</button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading && !errorMessage" class="loading-state">
    <div class="loading-spinner"></div>
    <p>Loading patient details...</p>
  </div>

  <!-- Content -->
  <ng-container *ngIf="patientDetails$ | async as details">
  <div *ngIf="details && details.payload && !errorMessage; else noData">

    <!-- Navigation Buttons -->
    <div class="navigation-buttons">
      <a class="back-link" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon> Back to Search Results
      </a>
      <a class="new-search-link" (click)="startNewSearch()">
        <mat-icon>search</mat-icon> New Search
      </a>
    </div>

    <!-- Patient Banner -->
    <div class="patient-banner">
      <div class="banner-left">
        <div class="patient-avatar">{{ getInitials(details.payload) }}</div>
        <div class="patient-name-section">
          <h1 class="patient-name">{{ details.payload.familyName }}, {{ details.payload.firstName }} {{ details.payload.middleName || '' }}</h1>
          <div class="patient-badges">
            <span class="status-badge" [class.active]="isPatientActive(details.payload)" [class.inactive]="!isPatientActive(details.payload)">
              {{ isPatientActive(details.payload) ? 'ACTIVE' : 'INACTIVE' }}
            </span>
            <span class="mpi-badge">MPI: {{ details.payload.masterPatientIdentifier }}</span>
            <span class="pingone-badge"
                  [ngClass]="getPingOneStatusClass()"
                  (click)="openPingOneModal(details.payload)"
                  matTooltip="Click to view PingOne account details">
              <mat-icon>account_circle</mat-icon>
              PingOne: {{ getPingOneStatusLabel() }}
            </span>
          </div>
        </div>
      </div>
      <div class="banner-buttons">
        <button class="back-btn" (click)="goBack()">Back to Results</button>
        <button class="new-search-btn" (click)="startNewSearch()">New Search</button>
      </div>
    </div>

    <!-- Quick Info Bar -->
    <div class="quick-info-bar">
      <div class="info-item">
        <span class="info-label">DATE OF BIRTH</span>
        <span class="info-value">{{ formatDate(details.payload.birthDate) }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">GENDER</span>
        <span class="info-value">{{ details.payload.gender | titlecase }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">PRIMARY LANGUAGE</span>
        <span class="info-value">{{ details.payload.primaryLanguageTermName || 'English' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">MARITAL STATUS</span>
        <span class="info-value">{{ details.payload.maritalStatusName || '—' }}</span>
      </div>
    </div>

    <!-- Two Column Content -->
    <div class="content-grid">
      <!-- Left Column -->
      <div class="left-column">
        <!-- Medical Information Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>medical_information</mat-icon>
            <h2>Medical Information</h2>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">Primary Diagnosis</span>
              <span class="value">
                {{ details.payload.primaryDiagnosisName || '—' }}
                <span class="code" *ngIf="details.payload.primaryDiagnosisCode">({{ details.payload.primaryDiagnosisCode }})</span>
              </span>
            </div>
            <div class="info-row">
              <span class="label">Renal Function Status</span>
              <span class="value">{{ details.payload.renalFunctionStatusName || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Current Modality</span>
              <span class="value">
                <ng-container *ngIf="getCurrentModality(details.payload) as modality; else noModality">
                  <strong>{{ modality.modalityDisplay }}</strong>
                  <span class="sub-text">Since {{ formatDate(modality.effectiveDate) }}</span>
                </ng-container>
                <ng-template #noModality>—</ng-template>
              </span>
            </div>
            <div class="info-row">
              <span class="label">First Dialysis Date</span>
              <span class="value">{{ formatDate(details.payload.firstDialysisEverDate) || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Regular Dialysis Begin</span>
              <span class="value">{{ formatDate(details.payload.regularChronicDialysisBegin) || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="label">IKC Patient</span>
              <span class="value">{{ details.payload.ikcPatientIndicator ? 'Yes' : 'No' }}</span>
            </div>
          </div>
        </div>

        <!-- Contact Information Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>phone</mat-icon>
            <h2>Contact Information</h2>
          </div>
          <div class="card-body">
            <div class="contact-section" *ngIf="details.payload.patientContacts?.length > 0; else noContacts">
              <div class="contact-group">
                <span class="contact-label">PHONE NUMBERS</span>
                <div class="contact-item" *ngFor="let contact of details.payload.patientContacts">
                  <mat-icon>phone</mat-icon>
                  <span>{{ contact.phoneNumber || contact.value }}</span>
                  <span class="contact-type" *ngIf="contact.phoneNumberType || contact.type">({{ contact.phoneNumberType || contact.type }})</span>
                </div>
              </div>
            </div>
            <ng-template #noContacts>
              <div class="contact-section">
                <span class="no-data">No contact information available</span>
              </div>
            </ng-template>

            <div class="contact-section" *ngIf="details.payload.patientAddresses?.length > 0; else noPatientAddress">
              <span class="contact-label">HOME ADDRESS</span>
              <div class="address-item" *ngFor="let address of details.payload.patientAddresses">
                <mat-icon>location_on</mat-icon>
                <div class="address-text">
                  <span>{{ address.addressLine1 || address.line1 }}</span>
                  <span *ngIf="address.addressLine2 || address.line2">{{ address.addressLine2 || address.line2 }}</span>
                  <span>{{ address.city }}, {{ address.state }} {{ address.postalCode || address.zip }}</span>
                </div>
              </div>
            </div>
            <ng-template #noPatientAddress>
              <div class="contact-section">
                <span class="no-data">No address information available</span>
              </div>
            </ng-template>

            <div class="info-row">
              <span class="label">Translator Needed</span>
              <span class="value">{{ details.payload.translatorNeeded ? 'Yes' : 'No' }}</span>
            </div>
          </div>
        </div>

        <!-- Emergency Contacts Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>people</mat-icon>
            <h2>Emergency Contacts</h2>
          </div>
          <div class="card-body">
            <ng-container *ngIf="getGroupedEmergencyContacts(details.payload) as contacts">
              <div *ngIf="contacts.length > 0; else noEmergencyContacts">
                <div class="emergency-contact-card" *ngFor="let contact of contacts">
                  <!-- Name -->
                  <div class="contact-name">
                    {{
                    contact.firstName && contact.lastName
                    ? (contact.firstName + ' ' + contact.lastName)
                    : (contact.firstName || contact.lastName || 'Not Available')
                    }}
                  </div>
                  <!-- Tags -->
                  <div class="contact-tags">
                    <span class="contact-tag primary" *ngIf="contact.types.includes('Primary Emergency Contact')">
                      Primary Emergency Contact
                    </span>
                    <span class="contact-tag alternate" *ngIf="contact.types.includes('Alternate Emergency Contact')">
                      Alternate Emergency Contact
                    </span>
                    <span class="contact-tag disaster" *ngIf="contact.types.includes('In Case of Disaster Contact')">
                      In Case of Disaster Contact
                    </span>
                  </div>
                  <!-- Relationship -->
                  <div class="contact-relationship">
                    {{ contact.relationship || 'Not Available' }}
                  </div>
                  <!-- Phone -->
                  <div class="contact-phone">
                    <mat-icon>phone</mat-icon>
                    <span>{{ contact.phone || 'Not Available' }}</span>
                  </div>
                </div>
              </div>
            </ng-container>
            <ng-template #noEmergencyContacts>
              <span class="no-data">No emergency contacts available</span>
            </ng-template>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="right-column">
        <!-- Current Facility Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>business</mat-icon>
            <h2>Current Facility</h2>
          </div>
          <div class="card-body">
            <ng-container *ngIf="getActiveFacilityDetails(details.payload) as facility; else noActiveFacility">
              <div class="facility-main">
                <span class="facility-name">DaVita Facility #{{ facility.facilityNumber }}</span>
                <div class="facility-details">
                  <span class="facility-number">Facility #: {{ facility.facilityNumber }}</span>
                  <span class="facility-type">Type: {{ facility.homeClinic ? 'Home' : 'In-Center' }}</span>
                </div>
                <span class="facility-date">From Date: {{ formatDate(facility.fromDate) }}</span>
              </div>
            </ng-container>
            <ng-template #noActiveFacility>
              <span class="no-data">No active facility</span>
            </ng-template>
          </div>
        </div>

        <!-- Facility History Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>history</mat-icon>
            <h2>Facility History</h2>
          </div>
          <div class="card-body">
            <div *ngIf="details.payload.patientFacilities && details.payload.patientFacilities.length > 0; else noFacilityHistory">
              <div class="history-item" *ngFor="let facility of details.payload.patientFacilities">
                <div class="history-main">
                  <span class="history-name">Facility #{{ facility.facilityNumber }}</span>
                  <span class="history-number" *ngIf="facility.homeClinic">Home Clinic</span>
                </div>
                <span class="history-dates">{{ formatDate(facility.fromDate) }} - {{ facility.toDate ? formatDate(facility.toDate) : 'Present' }}</span>
              </div>
            </div>
            <ng-template #noFacilityHistory>
              <span class="no-data">No facility history available</span>
            </ng-template>
          </div>
        </div>

        <!-- Patient Identifiers Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>tag</mat-icon>
            <h2>Patient Identifiers</h2>
          </div>
          <div class="card-body">
            <ng-container *ngIf="details.payload.patientSourceIdentifiers">
              <div class="identifier-row" *ngFor="let identifier of details.payload.patientSourceIdentifiers">
                <span class="label">
                  <span>{{ identifier.display || identifier.system }}</span>
                  <span *ngIf="identifier.display === 'Reggie MPI'" [class]="identifier.use === 'old' ? 'no-badge' : 'yes-badge'">{{ identifier.use === 'old' ? 'inactive' : identifier.use }}</span>
                </span>
                <span class="value identifier">{{ identifier.patientSourceId }}</span>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Additional Information Card -->
        <div class="card">
          <div class="card-header">
            <mat-icon>info</mat-icon>
            <h2>Additional Information</h2>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">Citizenship</span>
              <span class="value">{{ details.payload.citizenShipName || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="label">HIE Opt-In</span>
              <span class="value">
                <span class="yes-badge" *ngIf="details.payload.hieOptInFlag">Yes</span>
                <span *ngIf="!details.payload.hieOptInFlag">No</span>
                <span class="date-inline" *ngIf="details.payload.hieOptInDate">{{ formatDate(details.payload.hieOptInDate) }}</span>
              </span>
            </div>
            <div class="info-row" *ngIf="details.payload.anticipatedRestartDate">
              <span class="label">Anticipated Restart</span>
              <span class="value">{{ formatDate(details.payload.anticipatedRestartDate) }}</span>
            </div>
            <div class="info-row" *ngIf="details.payload.raceDisplay">
              <span class="label">Race</span>
              <span class="value">{{ details.payload.raceDisplay }}</span>
            </div>
            <div class="info-row" *ngIf="details.payload.transplantFlag">
              <span class="label">Transplant</span>
              <span class="value">{{ details.payload.transplantFlag }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <ng-template #noData>
    <div class="error-state" *ngIf="!isLoading && !errorMessage">
      <mat-icon>warning</mat-icon>
      <h3>Could not load patient details</h3>
      <p>Please try again or go back to search.</p>
      <button class="back-btn" (click)="goBack()">Back to Search</button>
    </div>
  </ng-template>
  </ng-container>
</div>
